import { SLACK_ERROR_COPY } from '@/containers/integrations/connect-error-copy.util';
import { finishConnectPopup } from '@/containers/integrations/connect-popup.util';
import {
  clearPendingSlackLinkToken,
  consumeSlackOAuthOutcome,
  getPendingSlackLinkToken,
} from '@/lib/slack-callback-url';
import { useAppDispatch } from '@/store';
import {
  slackApi,
  useCompleteSlackLinkMutation,
  usePreviewSlackLinkMutation,
  type SlackLinkPreview,
} from '@/store/features/slack.feature';
import { useOrganization, useUser } from '@clerk/react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { BRAND } from '@/lib/brand';

const MAX_LINK_ATTEMPTS = 3;
const RETRY_DELAYS_MS = [250, 750];

type ApiError = {
  status?: number | string;
  data?: unknown;
};

type ParsedApiError = {
  status?: number | string;
  code?: string;
  message?: string;
  reason?: string;
};

const SLACK_LINK_DENIAL_COPY: Record<string, string> = {
  wrong_organization: `Sign in to the ${BRAND.name} organization connected to this Slack workspace, then mention @${BRAND.name} again.`,
  configuration_missing: `The ${BRAND.name} Slack app has not been configured in this environment yet.`,
  membership_unverified: `Your organization membership could not be verified. Mention @${BRAND.name} again after your membership is active.`,
  local_user_missing: `Your ${BRAND.name} user record is not ready yet.`,
  local_user_deleted: `This ${BRAND.name} user can no longer connect Slack.`,
  local_organization_mismatch: `Your ${BRAND.name} account is not in the organization connected to this Slack workspace.`,
  organization_unavailable: `The ${BRAND.name} organization is not available.`,
  organization_suspended: `This ${BRAND.name} organization is suspended.`,
  appsumo_not_supported:
    'Slack Agent is not included with AppSumo-only access.',
  feature_gated: 'Slack Agent is available on Pro and Enterprise.',
  upgrade_required: 'Slack Agent is available on Pro and Enterprise.',
  execution_disabled: 'Slack execution is temporarily disabled.',
};

function parseApiError(error: unknown): ParsedApiError {
  if (!error || typeof error !== 'object') return {};
  const apiError = error as ApiError;
  const parsed: ParsedApiError = { status: apiError.status };
  if (!apiError.data || typeof apiError.data !== 'object') return parsed;

  const envelope = apiError.data as Record<string, unknown>;
  const detail = envelope.detail;
  if (typeof detail === 'string') {
    parsed.message = detail;
  } else if (detail && typeof detail === 'object') {
    const payload = detail as Record<string, unknown>;
    if (typeof payload.error === 'string') parsed.code = payload.error;
    if (typeof payload.message === 'string') parsed.message = payload.message;
  }

  // The shared expected-organization guard uses the platform error envelope,
  // while the Slack route uses FastAPI's detail envelope. Support both so an
  // org/JWT transition cannot be mistaken for an expired one-time link.
  const platformError = envelope.error;
  if (typeof platformError === 'string' && !parsed.code) {
    parsed.code = platformError;
  } else if (platformError && typeof platformError === 'object') {
    const payload = platformError as Record<string, unknown>;
    if (typeof payload.code === 'string' && !parsed.code) {
      parsed.code = payload.code;
    }
    if (typeof payload.message === 'string' && !parsed.message) {
      parsed.message = payload.message;
    }
    const details = payload.details;
    if (details && typeof details === 'object') {
      const reason = (details as Record<string, unknown>).reason;
      if (typeof reason === 'string') parsed.reason = reason;
    }
  }
  return parsed;
}

function isRetryable(error: unknown): boolean {
  const { status, code } = parseApiError(error);
  return (
    code === 'link_completion_in_progress' ||
    status === 'FETCH_ERROR' ||
    status === 'TIMEOUT_ERROR' ||
    status === 'PARSING_ERROR' ||
    status === 429 ||
    (typeof status === 'number' && status >= 500)
  );
}

function isOrganizationTransition(error: unknown): boolean {
  const parsed = parseApiError(error);
  return (
    parsed.status === 409 && parsed.reason === 'active_organization_changed'
  );
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function terminalErrorCopy(error: unknown): string {
  const { status, code, message } = parseApiError(error);
  if (code && SLACK_LINK_DENIAL_COPY[code]) {
    return SLACK_LINK_DENIAL_COPY[code];
  }
  if (status === 400) {
    return (
      message ??
      `Check Slack for the confirmation DM. If it is not there, mention @${BRAND.name} again for a fresh link.`
    );
  }
  if (status === 403) {
    return (
      message ??
      `Sign in to the ${BRAND.name} organization connected to this Slack workspace, then mention @${BRAND.name} again.`
    );
  }
  return (
    message ??
    `This Slack link could not be completed. Mention @${BRAND.name} again for a fresh link.`
  );
}

/**
 * Completes the browser half of Slack account linking after Clerk auth.
 * RootContent mounts this independently of the route the auth flow lands on.
 */
export function useSlackLinkCompletion() {
  const { isLoaded, isSignedIn, user } = useUser();
  const { isLoaded: isOrganizationLoaded, organization } = useOrganization();
  const organizationId = organization?.id;
  const onboardingComplete =
    user?.unsafeMetadata?.completed_onboarding === true;
  const dispatch = useAppDispatch();
  const [completeSlackLink] = useCompleteSlackLinkMutation();
  const [previewSlackLink] = usePreviewSlackLinkMutation();
  const [retryVersion, setRetryVersion] = useState(0);
  const [link, setLink] = useState<{
    key: string;
    preview?: SlackLinkPreview;
    error?: string;
    busy: boolean;
    // A terminal failure discards the one-time token; its explanation must
    // still stay on screen until the user dismisses it.
    terminal?: boolean;
  } | null>(null);
  const token = getPendingSlackLinkToken();
  const ready =
    isLoaded &&
    isSignedIn &&
    isOrganizationLoaded &&
    !!organizationId &&
    onboardingComplete &&
    !!user?.id &&
    !!token;
  const key = ready ? JSON.stringify([token, organizationId, user?.id]) : '';
  // Sync after commit, not during render: a discarded concurrent render must
  // not move the key ahead of the effect that actually issued the preview,
  // or that request's valid response would be dropped with busy stuck on.
  const currentKey = useRef(key);
  useEffect(() => {
    currentKey.current = key;
  }, [key]);
  const completing = useRef(false);

  useEffect(() => {
    if (
      !isLoaded ||
      !isSignedIn ||
      !isOrganizationLoaded ||
      !organizationId ||
      !onboardingComplete
    ) {
      return;
    }
    const outcome = consumeSlackOAuthOutcome();
    if (!outcome) return;
    // A window Clu's card opened tells the card and closes.
    finishConnectPopup(
      !outcome.error && !!outcome.connected,
      outcome.error ?? undefined
    );

    if (outcome.error) {
      toast.error(
        SLACK_ERROR_COPY[outcome.error] ??
          `Slack connection failed (${outcome.error}).`
      );
    }
    if (outcome.connected) {
      toast.success('Slack connected.');
      dispatch(
        slackApi.util.invalidateTags([
          { type: 'SlackIntegration', id: `STATUS:${organizationId}` },
        ])
      );
    }
  }, [
    dispatch,
    isLoaded,
    isOrganizationLoaded,
    isSignedIn,
    onboardingComplete,
    organizationId,
  ]);

  useEffect(() => {
    if (!key || !token || !organizationId) return;
    let cancelled = false;
    setLink({ key, busy: true });
    void previewSlackLink({ browserToken: token, organizationId })
      .unwrap()
      .then((preview) => {
        if (!cancelled && currentKey.current === key) {
          setLink({ key, preview, busy: false });
        }
      })
      .catch((error) => {
        if (cancelled || currentKey.current !== key) return;
        const transient = isRetryable(error) || isOrganizationTransition(error);
        const terminal = !transient && parseApiError(error).status !== 401;
        if (terminal) clearPendingSlackLinkToken(token);
        setLink({
          key,
          busy: false,
          terminal,
          error: transient
            ? 'Could not load the account details. Your Slack link is saved. Try again.'
            : terminalErrorCopy(error),
        });
      });
    return () => {
      cancelled = true;
    };
  }, [key, token, organizationId, previewSlackLink, retryVersion]);

  // Clearing the token empties `key`, so a terminal failure is matched by its
  // flag instead of by key; everything else must belong to the current key.
  const visible =
    link && (link.key === key || (link.terminal && !key)) ? link : null;
  const confirm = async () => {
    if (!visible?.preview || !token || !organizationId || completing.current)
      return;
    const preview = visible.preview;
    const confirmedKey = key;
    completing.current = true;
    setLink({ key, preview, busy: true });
    try {
      for (let attempt = 0; attempt < MAX_LINK_ATTEMPTS; attempt++) {
        if (currentKey.current !== confirmedKey) return;
        try {
          await completeSlackLink({
            browserToken: token,
            organizationId,
            slackUserId: preview.slackUserId,
            cleanlistUserId: preview.cleanlistUserId,
          }).unwrap();
          clearPendingSlackLinkToken(token);
          if (currentKey.current === confirmedKey) {
            setLink(null);
            toast.success(
              'Almost done. Confirm the link in the Slack DM we just sent you.'
            );
          }
          return;
        } catch (error) {
          if (currentKey.current !== confirmedKey) return;
          const transient =
            isRetryable(error) || isOrganizationTransition(error);
          if (transient && attempt + 1 < MAX_LINK_ATTEMPTS) {
            await wait(RETRY_DELAYS_MS[attempt] ?? 750);
            continue;
          }
          const terminal = !transient && parseApiError(error).status !== 401;
          if (terminal) clearPendingSlackLinkToken(token);
          setLink({
            key,
            preview,
            busy: false,
            terminal,
            error: transient
              ? 'Slack is temporarily unavailable. Your link is saved. Try again shortly.'
              : terminalErrorCopy(error),
          });
          return;
        }
      }
    } finally {
      completing.current = false;
    }
  };
  const cancel = () => {
    if (completing.current) return;
    if (token) clearPendingSlackLinkToken(token);
    setLink(null);
  };
  return {
    ...visible,
    open: !!visible,
    canRetry: !!visible?.error && !visible.terminal,
    cleanlistName:
      visible?.preview?.cleanlistEmail ||
      user?.primaryEmailAddress?.emailAddress ||
      user?.fullName ||
      user?.id,
    organizationName: organization?.name || organizationId,
    confirm,
    cancel,
    retry: () => setRetryVersion((value) => value + 1),
  };
}
